/**
 * 영업진행현황 — 목록 (Phase 6-1).
 *
 * 디자인:
 *   - 상단: 페이지 헤더 (제목 + 카운트 + 등록 버튼 placeholder)
 *   - 진행단계 6 탭 (전체/견적/유력/발주/납품/발행)
 *   - 통합 검색
 *   - 11 컬럼 표
 *     · 헤더 클릭 시 정렬 (asc → desc → 해제)
 *     · 헤더 호버 시 ▼ 아이콘 표시 → 클릭하면 정렬·필터 팝오버 (엑셀식 체크박스 + 검색)
 *     · 헤더 sticky top-0 (긴 표 스크롤 시 헤더 고정)
 *     · 행 줄무늬 + 행 호버 강조
 *   - 페이지 구분 없이 한 화면 스크롤 (FETCH_LIMIT 까지)
 *
 * 이후 단계: 행 클릭 시 상세 모달 (Phase 6-2), 등록·수정 폼 (Phase 6-3)
 *
 * v2.249.0: 화면 안 탭 둘 — [📋 영업 목록](이 파일의 ProgressTab) + [📄 판매보고서](pages/sales/ReportArchiveTab ·
 *   사내결재 문서함에서 옮겨 옴). 상세 창은 두 탭 공용으로 SalesListPage 에 한 벌. 자세한 건 SalesListPage 머리글.
 * v2.290.0: 가운데에 [🧾 세금계산서](pages/sales/TaxInvoiceTab · 거래 문서함에서 옮겨 옴) — 탭 셋.
 */

import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import * as salesApi from '../lib/sales-api';
import { STAGE_BADGE_CLASS } from '../lib/sales-stage';
import { isNextStepStale, shortDate } from '../lib/sales-next-step';
import * as prefsApi from '../lib/preferences-api';
import { resolveActiveColumns, resolveColumnOrder } from '../lib/columnPrefs';
import type { CompanyId, SalesListRow, SalesStatus, UserPreferences } from '../lib/types';
import {
  type Column,
  ColumnHeader,
  applyColState,
  useColState,
} from '../components/DataTable';
import { ExcelModeGroup } from '../components/ExcelModeButton';
import { YearDropdown } from '../components/YearDropdown';
import { LastUpdateBadge } from '../components/LastUpdateBadge';
import { VatNote } from '../components/VatNote';
import { SalesKpiStrip } from '../components/SalesKpiStrip';
import { SearchBar } from '../components/SearchBar';
import { CopyableText } from '../components/CopyableText';
import { ResetMenu } from '../components/ResetButton';
import { useAuth } from '../lib/auth-context';
import { listAttachments, previewAttachment } from '../lib/attachment-api';
import { quotePdfDownloadUrl } from '../lib/quote-api';
import { openServerPdfPreview } from '../lib/pdf-preview';
import { useFillHeight } from '../lib/useFillHeight';
import { PageTabs } from '../components/ui/PageTabs';
import { EmptyRow } from '../components/ui/EmptyState';
import { LoadingLines } from '../components/ui/Skeleton';

const AttachmentDownloadModal = lazy(() =>
  import('../components/AttachmentDownloadModal').then((m) => ({ default: m.AttachmentDownloadModal })),
);

// 모달은 lazy — 초기 번들에서 분리. 사용자가 행 클릭 / [+ 영업건 등록] 시 처음 로드.
// 매입 PurchaseList 와 동일 패턴 (ba9553c).
const SalesDetailModal = lazy(() =>
  import('../components/SalesDetailModal').then((m) => ({ default: m.SalesDetailModal })),
);
const SalesFormModal = lazy(() =>
  import('../components/SalesFormModal').then((m) => ({ default: m.SalesFormModal })),
);
const NtsSalesCheckModal = lazy(() =>
  import('../components/NtsSalesCheckModal').then((m) => ({ default: m.NtsSalesCheckModal })),
);
const TaxInvoiceRequestModal = lazy(() =>
  import('../components/TaxInvoiceRequestModal').then((m) => ({ default: m.TaxInvoiceRequestModal })),
);

/** 표시 순서 (좌→우) — 다중 토글 탭. '전체' 개념 없음, 6개 모두 활성=전체 표시. */
const ALL_STAGES: SalesStatus[] = ['일반', '유력', '발주', '납품', '발행', '판보'];

/** 사업자 다중 토글 — 둘 다 활성=전체 (companyId 미전달), 한쪽만=그 사업자, 0개=빈 결과. */
const ALL_COMPANIES: CompanyId[] = ['SM', 'EJ'];

/**
 * 영업진행 탭 첫 진입 시 활성화된 단계.
 * 발행/판보는 완료된 영업건이라 기본은 비활성 — 진행 중인 4개만 보임.
 * 사용자가 직접 토글하거나 '전체 표시' 링크로 6개 모두 켤 수 있음.
 */
const DEFAULT_ACTIVE_STAGES: SalesStatus[] = ['일반', '유력', '발주', '납품'];
const FETCH_LIMIT = 10000;

/**
 * 연도 필터 기본값 — 최근 **3개년** (v2.203.0 · 사용자 요청 「2개년 선택이 기본인데 3개년으로」).
 * ⭐첫 화면·자료 도착 후 보정·[필터 초기화 → 기본값] 세 자리가 **이 한 곳**을 본다
 *   (예전엔 세 자리에 「2」가 따로 박혀 있었다 — 하나만 고치면 초기화할 때 2개년으로 돌아간다).
 */
const DEFAULT_YEAR_COUNT = 3;

/** 기본 선택 연도 — 운영 자료의 연도(최근 순)에서 앞의 3개. 자료가 아직 없으면 올해부터 거꾸로 3개 */
function defaultYears(availableYears: string[]): Set<string> {
  if (availableYears.length > 0) return new Set(availableYears.slice(0, DEFAULT_YEAR_COUNT));
  const cy = new Date().getFullYear();
  return new Set(Array.from({ length: DEFAULT_YEAR_COUNT }, (_, i) => String(cy - i)));
}

/**
 * 진행단계 정렬 우선순위 — 백엔드 list.ts 의 ORDER BY 와 동일하게 유지.
 * 오름차순: 판보 → 발행 → 납품 → 발주 → 유력 → 일반.
 * 내림차순: 일반 → 유력 → ... → 판보.
 */
/**
 * 목록 💬 말풍선 글 — 「메모 n건 · 마지막: 이름 · 날짜」 + 내용 (v2.305.0 · #20).
 *   날짜는 상세 「메모」 칸과 같이 보는 사람 PC 시각으로 · 옛 평문 메모는 쓴 사람·날짜가 없다.
 */
function memoTooltip(r: SalesListRow): string {
  const m = r.lastMemo;
  if (!m) return '';
  let when = '';
  if (m.createdAt) {
    const d = new Date(m.createdAt);
    if (!Number.isNaN(d.getTime())) {
      when = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
  }
  const who = [m.userName, when].filter(Boolean).join(' · ');
  const n = r.memoCount ?? 1;
  return `💬 메모 ${n}건${who ? ` — 마지막: ${who}` : ''}\n${m.content}${n > 1 ? '\n(전체는 영업건 상세의 「메모」)' : ''}`;
}

const STATUS_RANK: Record<SalesStatus, number> = {
  판보: 1,
  발행: 2,
  납품: 3,
  발주: 4,
  유력: 5,
  일반: 6,
};

function fmtKrw(n: number): string {
  return n.toLocaleString('ko-KR');
}

/**
 * 영업진행 표 컬럼 정의.
 *
 * defaultOn=true → 새 사용자에게 기본 활성. UserPreferences.salesTabColumns 가 비어있으면 이 값 사용.
 * 사용자(sales/admin)는 토글로 변경 가능. viewer 는 토글 메뉴 X (기본값 그대로).
 * mobileHidden=true 컬럼은 모바일에서는 항상 숨김 (PC 토글과 무관).
 */
type SalesColumn = Column<SalesListRow> & {
  defaultOn: boolean;
  toggleLabel?: string;
};

/** 발주서 PDF 아이콘 — 계약일 셀 안에 표시. 1건당 1개 정책. 클릭 즉시 다운로드. */
function PurchaseOrderIcon({ parentKind, parentId }: { parentKind: 'sales' | 'bell'; parentId: string }) {
  async function onClick(e: React.MouseEvent) {
    e.stopPropagation();
    const list = await listAttachments(parentKind, parentId, '발주서');
    if (list[0]) previewAttachment(list[0]);
  }
  return (
    <button
      type="button"
      onClick={onClick}
      title="발주서 PDF 열기"
      className="text-blue-600 hover:text-blue-800"
    >
      📑
    </button>
  );
}

/** 분할납품요구서 PDF 셀 — 첨부 0건이면 비움, 1건이면 클릭 시 새 탭, 2건+이면 선택 모달. */
function SplitDeliveryCell({ row }: { row: SalesListRow }) {
  const [showPicker, setShowPicker] = useState(false);
  if (row.splitDeliveryCount === 0) {
    return <span className="text-slate-400 text-xs">-</span>;
  }
  async function onClick(e: React.MouseEvent) {
    e.stopPropagation(); // 행 클릭 (상세 모달) 방지
    if (row.splitDeliveryCount === 1) {
      const list = await listAttachments('sales', row.salesId, '분할납품요구서');
      if (list[0]) previewAttachment(list[0]);
    } else {
      setShowPicker(true);
    }
  }
  return (
    <>
      <button
        type="button"
        onClick={onClick}
        title={`PDF ${row.splitDeliveryCount}개 — 클릭하여 열기`}
        className="text-rose-600 hover:text-rose-800 text-base"
      >
        📄
      </button>
      {showPicker && (
        <Suspense fallback={null}>
          <AttachmentDownloadModal
            parentKind="sales"
            parentId={row.salesId}
            kind="분할납품요구서"
            title="📄 분납서 선택"
            onClose={() => setShowPicker(false)}
          />
        </Suspense>
      )}
    </>
  );
}

/** 납품확인증 스캔본(서명본) 아이콘 — 납품완료일 셀 안. 0건이면 미표시, 1건 즉시 열기, 2건+ 선택 모달. */
function DeliveryConfirmationCell({ row }: { row: SalesListRow }) {
  const [showPicker, setShowPicker] = useState(false);
  if (row.deliveryConfirmationScanCount === 0) return null;
  async function onClick(e: React.MouseEvent) {
    e.stopPropagation(); // 행 클릭 (상세 모달) 방지
    if (row.deliveryConfirmationScanCount === 1) {
      const list = await listAttachments('sales', row.salesId, '납품확인증');
      if (list[0]) previewAttachment(list[0]);
    } else {
      setShowPicker(true);
    }
  }
  return (
    <>
      <button
        type="button"
        onClick={onClick}
        title={`납품확인증 스캔본 ${row.deliveryConfirmationScanCount}개 — 클릭하여 열기`}
        className="text-emerald-600 hover:text-emerald-800 shrink-0"
      >
        🖊
      </button>
      {showPicker && (
        <Suspense fallback={null}>
          <AttachmentDownloadModal
            parentKind="sales"
            parentId={row.salesId}
            kind="납품확인증"
            title="🖊 납품확인증 스캔본 선택"
            onClose={() => setShowPicker(false)}
          />
        </Suspense>
      )}
    </>
  );
}

/** 세금계산서 PDF 아이콘 — 계산서 발행일 셀 안. 0건이면 미표시, 1건 즉시 열기, 2건+(수정세금계산서 이력) 선택 모달.
 *  viewer 는 taxInvoiceCount 가 0 으로 마스킹되어(공급가액 포함 문서) 자연히 미표시. */
function TaxInvoiceCell({ row }: { row: SalesListRow }) {
  const [showPicker, setShowPicker] = useState(false);
  if (row.taxInvoiceCount === 0) return null;
  async function onClick(e: React.MouseEvent) {
    e.stopPropagation(); // 행 클릭 (상세 모달) 방지
    if (row.taxInvoiceCount === 1) {
      const list = await listAttachments('sales', row.salesId, '세금계산서');
      if (list[0]) previewAttachment(list[0]);
    } else {
      setShowPicker(true);
    }
  }
  return (
    <>
      <button
        type="button"
        onClick={onClick}
        title={`세금계산서 PDF ${row.taxInvoiceCount}개 — 클릭하여 열기`}
        className="text-sky-600 hover:text-sky-800 shrink-0"
      >
        📑
        {row.taxInvoiceCount > 1 && (
          <sup className="ml-0.5 text-[11px] font-semibold text-sky-700">{row.taxInvoiceCount}</sup>
        )}
      </button>
      {showPicker && (
        <Suspense fallback={null}>
          <AttachmentDownloadModal
            parentKind="sales"
            parentId={row.salesId}
            kind="세금계산서"
            title="📑 세금계산서 PDF 선택"
            onClose={() => setShowPicker(false)}
          />
        </Suspense>
      )}
    </>
  );
}

const COLUMNS: SalesColumn[] = [
  {
    key: 'lastUpdate', label: 'Last Update', type: 'date',
    accessor: r => r.lastUpdate,
    render: r => <span className="text-slate-500 text-xs">{r.lastUpdate}</span>,
    mobileHidden: true, defaultOn: true,
  },
  {
    key: 'salesId', label: '매출일련번호', type: 'text',
    accessor: r => r.salesId,
    render: r => <strong className="text-blue-700">{r.salesId}</strong>,
    mobileHidden: true, defaultOn: false,
  },
  {
    key: 'empName', label: '담당', type: 'text',
    accessor: r => r.empName ?? r.empId,
    render: r => r.empName ?? r.empId,
    mobileHidden: true, defaultOn: true,
  },
  {
    key: 'companyId', label: '사업자', type: 'text', align: 'center',
    accessor: r => r.companyId,
    render: r => <span className="text-xs text-slate-600">{r.companyId}</span>,
    mobileHidden: true, defaultOn: false,
  },
  {
    key: 'invoiceCompany', label: '계산서 발행업체', type: 'text',
    accessor: r => r.invoiceCompany ?? '',
    mobileHidden: true, defaultOn: true,
  },
  {
    key: 'proposalCompany', label: '제안업체', type: 'text',
    accessor: r => r.proposalCompany ?? '',
    defaultOn: true,
  },
  {
    key: 'endUser', label: '엔드유저', type: 'text',
    accessor: r => r.endUser ?? '',
    render: r => (
      <span
        className="cell-link block max-md:max-w-[100px] max-md:truncate"
        title={r.endUser ?? ''}
      >
        {r.endUser ?? ''}
      </span>
    ),
    defaultOn: true,
  },
  {
    key: 'projectName', label: '프로젝트명', type: 'text',
    accessor: r => r.projectName ?? '',
    // 글자를 클릭하면 프로젝트명 전체가 클립보드에 복사된다(다른 문서에 붙여넣는 일이 잦아서).
    // 자르기(240px)는 바깥 div 가 그대로 맡고, 클릭 영역만 글자 길이에 맞춘다 —
    // 칸의 빈 자리를 누르면 원래대로 상세창이 열린다.
    render: r => (
      <div className="text-xs text-slate-700 truncate max-w-[240px]">
        <CopyableText value={r.projectName ?? ''} what="프로젝트명" />
      </div>
    ),
    mobileHidden: true, defaultOn: false,
  },
  {
    // 왼쪽 정렬(v2.317.1 · 사용자 「상태 표시가 옆 아이콘(📦🧾📄💬) 유무에 따라 틀어진다 → 칸 왼쪽 끝에 맞춰」) —
    // 가운데 정렬이면 아이콘 개수만큼 배지가 왼쪽으로 밀려 줄마다 자리가 달랐다.
    key: 'status', label: '진행', type: 'text', align: 'left',
    accessor: r => r.status,
    sortAccessor: r => STATUS_RANK[r.status] ?? 99,
    // 발주 + 계약일 미입력 = 구두 발주 → 빨강 채움 배지(.stage-alert · index.css)로 시각 구분.
    render: r => {
      const isVerbalOrder = r.status === '발주' && !r.contractDate;
      if (isVerbalOrder) {
        return (
          <span
            className="stage-badge stage-alert"
            title="구두 발주 (발주서 미접수 — 계약일 비어있음)"
          >
            발주⚠
          </span>
        );
      }
      // 출고 자동 납품 처리됨 — 매입목록 출고 또는 자유입력 매칭 모달로 자동 '납품' 변경된 영업건.
      // 사용자가 영업진행 화면에서 어떤 영업건이 자동 변경됐는지 시각적 추적.
      return (
        <span className="inline-flex items-center gap-0.5">
          <span className={STAGE_BADGE_CLASS[r.status]}>{r.status}</span>
          {r.autoDelivered && (
            <span
              className="text-[11px] cursor-help"
              title="출고 승인으로 자동 납품 처리된 영업건"
            >
              📦
            </span>
          )}
          {r.invoiceRequestAt && (
            <span
              className="text-[11px] cursor-help"
              title={`세금계산서 발행요청을 보낸 건 (${r.invoiceRequestAt.slice(0, 10)})`}
            >
              🧾
            </span>
          )}
          {/* 판보 작성 여부 구분 (v1.6.1) — 발행 단계에서 판보를 이미 올린 건 표시.
              '판보' 단계는 이미 최종승인이므로 표시 생략(중복 정보). */}
          {r.reportApprovalStatus && r.status !== '판보' && (
            <span
              className="text-[11px] cursor-help"
              title={
                `판보 결재: ${r.reportApprovalStatus}` +
                (r.reportBundleOf ? ` (${r.reportBundleOf} 판보에 묶임)` : '') +
                ' — 확인은 [내 결재함]에서'
              }
            >
              {r.reportApprovalStatus === '반려' ? '📄⛔' : '📄'}
            </span>
          )}
          {/* 💬 메모 미리보기 (v2.305.0 · 개발요청 #20 「엑셀 노트처럼」) — 마우스를 올리면 마지막(맨 위) 메모 */}
          {r.lastMemo && (
            <span className="text-[11px] cursor-help" title={memoTooltip(r)}>
              💬
            </span>
          )}
        </span>
      );
    },
    defaultOn: true,
  },
  {
    // v2.319.0 다음 할 일(Salesforce Next Step) — 글 + 예정일 · 7일 넘게 그대로면 ⏰(발행·판보는 제외)
    key: 'nextStep', label: '다음 할 일', type: 'text',
    accessor: r => r.nextStep ? `${r.nextStep}${r.nextStepDate ? ` (${r.nextStepDate})` : ''}` : '',
    render: r => r.nextStep ? (
      <span className="inline-flex items-center gap-1 max-w-[220px]">
        <span className="truncate" title={r.nextStep}>{r.nextStep}</span>
        {r.nextStepDate && <span className="text-xs text-slate-500 whitespace-nowrap">{shortDate(r.nextStepDate)}</span>}
        {isNextStepStale(r.nextStep, r.nextStepAt, r.status) && <span className="text-red-600 cursor-help" title="7일 넘게 그대로 — 다음 할 일을 다시 적어 주세요">⏰</span>}
      </span>
    ) : <span className="text-slate-400 text-xs">-</span>,
    mobileHidden: true, defaultOn: true,
  },
  {
    key: 'itemSummary', label: '제안품목', type: 'text',
    accessor: r => r.itemSummary ?? '',
    render: r => (
      <div
        className="text-xs text-slate-600 max-md:max-w-[90px] max-md:truncate min-[1920px]:truncate min-[1920px]:max-w-[320px]"
        title={r.itemSummary ?? ''}
      >
        {r.itemSummary ?? ''}
      </div>
    ),
    defaultOn: true,
  },
  {
    key: 'proposalPrice', label: '제안가', type: 'number', align: 'right',
    accessor: r => r.proposalPrice,
    // 연결된 견적서가 있으면 금액 왼쪽에 📄 아이콘 — 클릭 시 견적서 PDF 다운로드.
    render: r => (
      <span className="inline-flex items-center justify-end gap-1 num">
        {r.linkedQuoteId != null && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openServerPdfPreview({ pdfUrl: quotePdfDownloadUrl(r.linkedQuoteId!), filename: `견적서_${r.salesId}.pdf` });
            }}
            title="연결된 견적서 미리보기"
            className="text-rose-600 hover:text-rose-800 shrink-0"
          >
            📄
          </button>
        )}
        {fmtKrw(r.proposalPrice)}
      </span>
    ),
    defaultOn: true,
  },
  {
    key: 'contractDate', label: '계약일', type: 'date',
    accessor: r => r.contractDate ?? '',
    render: r => (
      <span className="text-slate-500 inline-flex items-center gap-1">
        {r.contractDate ?? '-'}
        {r.purchaseOrderCount > 0 && <PurchaseOrderIcon parentKind="sales" parentId={r.salesId} />}
      </span>
    ),
    mobileHidden: true, defaultOn: true, toggleLabel: '계약일 (발주서)',
  },
  {
    // 납품예정일 (v2.136.0 · 2026-09-08 사용자 요청) — 등록·수정 폼에서 받는 값이고 웍스 캘린더 일정의 기준이다.
    //   자리는 계약일 → 예정일 → 완료일 순(일이 일어나는 순서).
    //   ⭐**아직 안 온 건에만 색을 준다** — 운영 2,054건 중 2,035건은 예정일과 완료일의 글자가 똑같아서
    //     색이 없으면 같은 날짜가 두 번 찍힌 칸이 된다. 이 칼럼이 실제로 쓸모 있는 건 **완료일이 없는 건**이고,
    //     그 자리에 amber 를 쓰는 것이 이미 사내 관례다(요약 카드의 「(예정)」·대시보드 「납품 임박」·비상벨 납기).
    //     ⚠`!r.deliveryDate` 게이트를 빼면 지난 날짜라는 이유로 2,035행이 전부 색칠된다.
    //   시간(deliveryStartTime)은 표에 안 싣는다 — 폭을 넓히지 않으려는 것이고, 시간은 상세 화면에서 본다
    //     (목록 API 가 시간을 안 내려주기도 한다 — 넣으려면 서버 select 부터 늘려야 한다).
    key: 'expectedDeliveryDate', label: '납품예정일', type: 'date',
    accessor: r => r.expectedDeliveryDate ?? '',
    render: r => (
      <span className={r.expectedDeliveryDate && !r.deliveryDate ? 'text-amber-700' : 'text-slate-500'}>
        {r.expectedDeliveryDate ?? '-'}
      </span>
    ),
    mobileHidden: true, defaultOn: true,
  },
  {
    key: 'deliveryDate', label: '납품완료일', type: 'date',
    accessor: r => r.deliveryDate ?? '',
    render: r => (
      <span className="text-slate-500 inline-flex items-center gap-1">
        {r.deliveryDate ?? '-'}
        <DeliveryConfirmationCell row={r} />
      </span>
    ),
    defaultOn: true,
  },
  {
    key: 'invoiceDate', label: '계산서 발행일', type: 'date',
    accessor: r => r.invoiceDate ?? '',
    render: r => (
      <span className="text-slate-500 inline-flex items-center gap-1">
        {r.invoiceDate ?? '-'}
        <TaxInvoiceCell row={r} />
      </span>
    ),
    mobileHidden: true, defaultOn: true,
  },
  {
    key: 'contactPerson', label: '거래처 담당자', type: 'text',
    accessor: r => r.contactPerson ?? '',
    render: r => r.contactPerson
      ? <span className="text-xs text-slate-700">{r.contactPerson}</span>
      : <span className="text-slate-400 text-xs">-</span>,
    mobileHidden: true, defaultOn: false,
  },
  {
    key: 'parentSalesId', label: '분할 원래 건', type: 'text',
    accessor: r => r.parentSalesId ?? '',
    render: r => r.parentSalesId
      ? <span className="text-xs text-slate-500 font-mono">{r.parentSalesId}</span>
      : <span className="text-slate-400 text-xs">-</span>,
    mobileHidden: true, defaultOn: false,
  },
  {
    key: 'splitDelivery', label: '분납서', type: 'text', align: 'center',
    // 엑셀 출력: '있음' / '없음'
    accessor: r => r.splitDeliveryCount > 0 ? '있음' : '없음',
    render: r => <SplitDeliveryCell row={r} />,
    mobileHidden: true, defaultOn: true,
  },
];

/** viewer 권한이 볼 수 없는 컬럼 키 — 금액성. 표시 컬럼 메뉴에서도 제외. */
const VIEWER_HIDDEN_KEYS = new Set<string>(['proposalPrice']);

// 2026-07-19 메뉴 재구성: 유지보수·비상벨 탭을 독립 메뉴(/maintenance·/bell)로 승격.
// 이 페이지는 영업 파이프라인(영업진행)만 담당.
//   v2.249.0 (2026-10-14 사용자 결정): 그 파이프라인의 **결과물인 판매보고서**(판보 보관함)를 사내결재 문서함에서 옮겨 와
//   탭이 둘이 됐다 — [📋 영업 목록] [📄 판매보고서]. 판보는 영업진행 건에만 있다(유지보수·비상벨은 별도 장부라 판보가 없다).
//   메뉴 줄을 늘리지 않으려는 결정이다(사용자 「메뉴가 너무 많아지는 게 싫다」). 결재(진행 중)는 그대로 [내 결재함].
//   v2.290.0 (2026-10-22 사용자 「거래문서함의 세금계산서 메뉴를 영업진행의 영업목록과 판매보고서 사이로」): 거래 문서함의
//   [세금계산서] 탭(발행요청 이력 · 수신·처리 내역)도 이리 옮겨 탭이 셋 — 발행요청은 영업 목록에서 보내고 계산서는 영업건에 붙는다.

/**
 * 영업진행 화면 안의 탭 키 — check:menu 가 이 줄을 읽어 탭 제목 표(lib/menu.ts TAB_TITLES)와 맞춰 본다.
 *   ⭐목록 탭은 주소에 tab 을 싣지 않는다(`/sales`) — 다른 화면이 보내는 `/sales?open=매출번호`(딜등록·원가분석·영업건 상세)가
 *   그대로 목록 탭에서 열리고, 사이드바 「영업진행」 도 목록 탭으로 온다. 다른 탭만 `?tab=taxInvoice` · `?tab=report`.
 */
type SalesPageTab = 'list' | 'taxInvoice' | 'report';
const PAGE_TABS: { key: SalesPageTab; label: string; salesOnly?: boolean }[] = [
  { key: 'list', label: '📋 영업 목록' },
  // 공급가액·고객사가 든 문서라 viewer 숨김 — 서버(/sales/tax-invoice-*)도 sales 이상만 (화면 숨김 ≠ 서버 차단)
  { key: 'taxInvoice', label: '🧾 세금계산서', salesOnly: true },
  // 원가·이익이 든 문서라 viewer 숨김 — 서버(/sales/report-archive)도 sales 이상만 (화면 숨김 ≠ 서버 차단)
  { key: 'report', label: '📄 판매보고서', salesOnly: true },
];

/** 주소의 tab 값 → 탭. 모르는 값 · viewer 의 숨긴 탭은 목록 탭(null = 목록) */
function tabFromParam(v: string | null, isViewer: boolean): SalesPageTab | null {
  const t = PAGE_TABS.find((x) => x.key === v && x.key !== 'list');
  return t && !(isViewer && t.salesOnly) ? t.key : null;
}

// 세금계산서·판매보고서 탭은 누를 때 처음 불러온다 — 영업진행 첫 화면 속도는 그대로
const TaxInvoiceTab = lazy(() =>
  import('./sales/TaxInvoiceTab').then((m) => ({ default: m.TaxInvoiceTab })),
);
const ReportArchiveTab = lazy(() =>
  import('./sales/ReportArchiveTab').then((m) => ({ default: m.ReportArchiveTab })),
);

export function SalesListPage() {
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';
  // 미매칭 표시 (v2.311.0 · 개발요청 #25 권애영 「웍스 메시지를 놓칠 때가 있어」) — 봇이 못 붙인 계산서 수를 탭 이름에 빨갛게.
  //   1분마다 다시 읽는다(셈 하나 · 탭을 안 열어도 보이게) · [🔗 영업건 지정]·[표시에서 빼기]는 이 값을 바로 다시 읽게 한다
  const unmatched = useQuery({
    queryKey: ['taxInvoiceUnmatchedCount'],
    queryFn: salesApi.getTaxInvoiceUnmatchedCount,
    enabled: !isViewer,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const unmatchedN = unmatched.data ?? 0;
  const tabs = PAGE_TABS.filter((t) => !(isViewer && t.salesOnly)).map((t) => (t.key === 'taxInvoice' && unmatchedN > 0
    ? { ...t, badge: unmatchedN, title: `봇이 영업건을 못 찾은 세금계산서 ${unmatchedN}장 — [수신·처리 내역]에서 [🔗 영업건 지정]으로 붙여 주세요` }
    : t));
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: SalesPageTab = tabFromParam(tabParam, isViewer) ?? 'list';
  // 한 번 연 탭은 숨겨 두고 그대로 둔다 — 판매보고서를 보고 돌아와도 목록의 단계·검색·필터·스크롤이 남는다.
  //   (탭을 바꿀 때마다 새로 그리면 목록에서 고른 값이 전부 처음으로 돌아간다 — 그 값이 전부 ProgressTab 안에 있다)
  const seen = useRef(new Set<SalesPageTab>());
  seen.current.add(tab);

  // 영업건 상세 — 두 탭이 **같은 창 한 벌**을 쓴다(판매보고서 탭의 매출번호도 여기로).
  //   숨겨 둔 탭 안에서 띄우면 그 탭과 함께 안 보이게 되므로(창이 display:none 안에 갇힌다) 화면 맨 바깥에 둔다.
  const [openSalesId, setOpenSalesId] = useState<string | null>(null);

  // 주소 → 화면 (한 번 읽고 주소에서 지운다 · replace 라 뒤로가기 역사에 안 쌓인다)
  //   ① `?open=매출번호` — 딜등록 상세·원가분석·영업건 상세의 「영업진행 건」 링크 (v2.143.0 · check:menu ⑨)
  //   ② tab 정리 — 목록 탭은 tab 없이. `tab=list`·모르는 값·viewer 의 숨긴 탭(taxInvoice·report)은 지운다.
  useEffect(() => {
    const open = searchParams.get('open');
    const badTab = tabParam !== null && tabFromParam(tabParam, isViewer) === null;
    if (!open && !badTab) return;
    if (open) setOpenSalesId(open);
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    if (badTab) next.delete('tab');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isViewer]);

  const selectTab = (t: SalesPageTab) => {
    const next = new URLSearchParams(searchParams);
    if (t === 'list') next.delete('tab');
    else next.set('tab', t);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="p-8">
      {/* v2.203.0: 제목 줄 오른쪽에 「현재 기준」 숫자 3개 — 필터와 무관(사용자 결정).
          v2.252.0: 세부 탭은 공용 PageTabs(밑줄) 로 제목 아래 한 줄 — 표 높이는 ProgressTab 이 표 위치를 재서 맞춘다
          (lib/useFillHeight — 예전 고정 계산 100vh - 320px 은 밀렸다). */}
      <div className="flex items-center justify-between gap-x-6 gap-y-2 mb-3 flex-wrap">
        <h1 className="ui-page-title">영업진행</h1>
        {/* 「현재 기준」 숫자 3개는 영업 목록의 것 — 판매보고서 탭에는 보관함 자체 합계(매출·원가·손익)가 따로 있다 */}
        {tab === 'list' && <SalesKpiStrip />}
      </div>
      {tabs.length > 1 && (
        <PageTabs tabs={tabs} value={tab} onChange={selectTab} ariaLabel="영업진행 보기" className="mb-3" />
      )}
      {seen.current.has('list') && (
        <div className={tab === 'list' ? undefined : 'hidden'}>
          <ProgressTab active={tab === 'list'} onOpen={setOpenSalesId} />
        </div>
      )}
      {seen.current.has('taxInvoice') && (
        <div className={tab === 'taxInvoice' ? undefined : 'hidden'}>
          <Suspense fallback={<div className="py-8 text-center text-sm text-slate-400"><LoadingLines /></div>}>
            <TaxInvoiceTab />
          </Suspense>
        </div>
      )}
      {seen.current.has('report') && (
        <div className={tab === 'report' ? undefined : 'hidden'}>
          <Suspense fallback={<div className="py-8 text-center text-sm text-slate-400"><LoadingLines /></div>}>
            <ReportArchiveTab onOpenSales={setOpenSalesId} />
          </Suspense>
        </div>
      )}
      {/* 상세 모달 (Phase 6-2) — lazy · 목록·판매보고서 탭 공용 */}
      <Suspense fallback={null}>
        {openSalesId && (
          <SalesDetailModal
            salesId={openSalesId}
            onClose={() => setOpenSalesId(null)}
          />
        )}
      </Suspense>
    </div>
  );
}

function ProgressTab({ active, onOpen }: {
  /** 지금 보이는 탭인가 — 숨겨 두었다가 다시 보일 때 표 높이를 다시 잰다 */
  active: boolean;
  /** 영업건 상세 열기 — 창은 SalesListPage 가 한 벌만 띄운다 */
  onOpen: (salesId: string) => void;
}) {
  const qc = useQueryClient();
  // 다중 토글 — 활성화된 단계의 행만 표시. 기본은 발행/판보 제외 4개 활성(DEFAULT_ACTIVE_STAGES).
  const [activeStages, setActiveStages] = useState<Set<SalesStatus>>(
    () => new Set(DEFAULT_ACTIVE_STAGES),
  );
  // 📄 판보 미작성만 (v2.305.0 · 개발요청 #20) — 켜면 서버가 진행단계 토글 대신 「발행 · 판보 없음」으로 고른다
  const [reportMissing, setReportMissing] = useState(false);
  // 사업자 다중 토글 — 첫 진입은 둘 다 활성 (전체).
  const [activeCompanies, setActiveCompanies] = useState<Set<CompanyId>>(
    () => new Set(ALL_COMPANIES),
  );
  // 연도 필터 — 서버측 필터로 데이터 양 자체를 줄임 (매입과 동일 패턴).
  // default = 올해부터 3개년 → 즉시 list fetch 시작. years 응답이 오면 운영 데이터의 최근 3개로 보정 (v2.203.0 · 전엔 2개).
  // 매입 동일: years/list 병렬 round-trip 절감.
  const [selectedYears, setSelectedYears] = useState<Set<string>>(() => defaultYears([]));
  const yearsInitializedRef = useRef(false);
  const [q, setQ] = useState('');
  const colState = useColState();
  // 표 상자 높이 — 표 위치를 재서 창 아래 끝까지 (v2.249.0 · lib/useFillHeight 머리글)
  const tableBoxRef = useRef<HTMLDivElement>(null);
  const fillHeight = useFillHeight(tableBoxRef, { active });
  // 상세 창·`?open=매출번호` 읽기는 SalesListPage 로 올렸다 (v2.249.0 — 판매보고서 탭과 같은 창을 쓴다)
  const [creating, setCreating] = useState(false);
  const [excelDownloading, setExcelDownloading] = useState(false);
  // 국세청 매출계산서 발행내역 대조 (확인 전용 — 쓰기 없음)
  const [ntsCheckOpen, setNtsCheckOpen] = useState(false);
  // 세금계산서 발행요청 — 선택 모드 + 선택된 영업건 + 발행요청 모달
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [requestOpen, setRequestOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const { user } = useAuth();
  const canCreate = user?.role === 'sales' || user?.role === 'mgmt' || user?.role === 'admin';
  const canNtsCheck = user?.role === 'mgmt' || user?.role === 'admin';
  const isViewer = user?.role === 'viewer';

  // ── 📐 내 표 설정 — preferences 로 영구 저장 (PC 만 적용; 모바일에선 default 표출) ──
  // viewer 도 사용 가능. 단 VIEWER_HIDDEN_KEYS 컬럼은 풀세트에서 제외.
  const prefsQ = useQuery({
    queryKey: ['preferences'],
    queryFn: prefsApi.getPreferences,
    staleTime: 5 * 60 * 1000,
  });
  const patchPrefs = useMutation({
    mutationFn: (next: Partial<UserPreferences>) => prefsApi.patchPreferences(next),
    onSuccess: (newPrefs) => qc.setQueryData(['preferences'], newPrefs),
  });
  // ExcelMode = 편집 모드 flag (드래그·resize·⚙ 노출). visibility/order/widths 적용은 prefs 존재 여부로 결정.
  const excelMode = !!prefsQ.data?.salesTabExcelMode;

  // 권한 기반 풀세트 — viewer 면 금액성 컬럼 제외.
  const fullColumns = useMemo<SalesColumn[]>(
    () => (isViewer ? COLUMNS.filter((c) => !VIEWER_HIDDEN_KEYS.has(c.key)) : COLUMNS),
    [isViewer],
  );
  const fullAllKeys = useMemo(() => fullColumns.map((c) => c.key), [fullColumns]);
  const fullDefaultKeys = useMemo(
    () => fullColumns.filter((c) => c.defaultOn).map((c) => c.key),
    [fullColumns],
  );

  // visibility / order / widths — ExcelMode 와 무관, prefs 가 있으면 적용. (없으면 default)
  const activeColKeys = useMemo<Set<string>>(
    () =>
      resolveActiveColumns(
        prefsQ.data?.salesTabColumns,
        prefsQ.data?.salesTabColumnsKnown,
        fullAllKeys,
        fullDefaultKeys,
      ),
    [prefsQ.data, fullAllKeys, fullDefaultKeys],
  );

  const orderedKeys = useMemo<string[]>(
    () => resolveColumnOrder(prefsQ.data?.salesTabColumnOrder, fullAllKeys),
    [prefsQ.data, fullAllKeys],
  );

  const visibleColumns = useMemo<SalesColumn[]>(() => {
    const map = new Map(fullColumns.map((c) => [c.key, c]));
    return orderedKeys
      .filter((k) => activeColKeys.has(k))
      .map((k) => map.get(k))
      .filter((c): c is SalesColumn => !!c);
  }, [orderedKeys, activeColKeys, fullColumns]);

  const columnWidths = prefsQ.data?.salesTabColumnWidths;
  const hasPrefs =
    !!prefsQ.data &&
    (!!prefsQ.data.salesTabColumns?.length ||
      !!prefsQ.data.salesTabColumnOrder?.length ||
      !!prefsQ.data.salesTabColumnWidths ||
      !!prefsQ.data.salesTabExcelMode);

  function toggleColumn(key: string) {
    const next = new Set(activeColKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    patchPrefs.mutate({
      salesTabColumns: [...next],
      salesTabColumnsKnown: [...fullAllKeys],
    });
  }
  function handleReorder(fromKey: string, toKey: string, position: 'before' | 'after') {
    const filtered = orderedKeys.filter((k) => k !== fromKey);
    const idx = filtered.indexOf(toKey);
    if (idx < 0) return;
    filtered.splice(position === 'before' ? idx : idx + 1, 0, fromKey);
    patchPrefs.mutate({ salesTabColumnOrder: filtered });
  }
  function handleMoveLeft(key: string) {
    const visKeys = visibleColumns.map((c) => c.key);
    const idx = visKeys.indexOf(key);
    if (idx <= 0) return;
    const prev = visKeys[idx - 1];
    if (prev) handleReorder(key, prev, 'before');
  }
  function handleMoveRight(key: string) {
    const visKeys = visibleColumns.map((c) => c.key);
    const idx = visKeys.indexOf(key);
    if (idx < 0 || idx >= visKeys.length - 1) return;
    const next = visKeys[idx + 1];
    if (next) handleReorder(key, next, 'after');
  }
  function handleResize(key: string, widthPx: number) {
    const next = { ...(columnWidths ?? {}), [key]: widthPx };
    patchPrefs.mutate({ salesTabColumnWidths: next });
  }
  function handleExcelModeReset() {
    // 모든 prefs 키 제거 (백엔드 PATCH 의 null = 키 삭제) + ExcelMode OFF.
    patchPrefs.mutate({
      salesTabExcelMode: false,
      salesTabColumns: null as unknown as undefined,
      salesTabColumnsKnown: null as unknown as undefined,
      salesTabColumnOrder: null as unknown as undefined,
      salesTabColumnWidths: null as unknown as undefined,
    });
  }
  const [colMenuOpen, setColMenuOpen] = useState(false);

  const allOn = activeStages.size === ALL_STAGES.length;
  const allOff = activeStages.size === 0;

  // 6개 모두 활성=전체 → 백엔드에 stage 미전달. 그 외엔 활성 단계만.
  // 전체 해제 시에도 미전달 — ❗이상 건만 남기는 클라이언트 필터로 처리 (baseRows 참조).
  const stageFilter: SalesStatus[] | undefined = allOn || allOff
    ? undefined
    : ALL_STAGES.filter((s) => activeStages.has(s));

  // 사업자 — 둘 다 켜져 있으면 undefined (전체). 한쪽만이면 그 값. 둘 다 꺼지면 빈 결과 (아래 enabled 로 차단).
  const allCompaniesOn = activeCompanies.size === ALL_COMPANIES.length;
  const allCompaniesOff = activeCompanies.size === 0;
  const companyFilter: CompanyId | undefined = allCompaniesOn || allCompaniesOff
    ? undefined
    : [...activeCompanies][0]!;

  // 사용 가능한 연도 — 별도 endpoint (stage/검색 필터와 독립적, 5분 캐시)
  const yearsQuery = useQuery({
    queryKey: ['sales', 'years'],
    queryFn: () => salesApi.listSalesYears(),
    staleTime: 5 * 60 * 1000,
  });
  const availableYears = yearsQuery.data ?? [];

  // availableYears 로드되면 1회 운영 데이터 최근 3개로 보정 (default 와 같으면 변화 없음 → 추가 fetch X)
  useEffect(() => {
    if (!yearsInitializedRef.current && availableYears.length > 0) {
      setSelectedYears(defaultYears(availableYears));
      yearsInitializedRef.current = true;
    }
  }, [availableYears]);

  // 백엔드에 보낼 연도 배열 — 빈 Set 이면 undefined (= 서버 필터 없음 / 전체)
  const yearArr = useMemo<number[] | undefined>(() => {
    if (selectedYears.size === 0) return undefined;
    return [...selectedYears].map((y) => Number(y)).filter((n) => Number.isFinite(n));
  }, [selectedYears]);

  const query = useQuery({
    // queryKey 에 정렬된 연도 배열 포함 (Set 순서 변동 방지)
    queryKey: ['sales', { stage: reportMissing ? null : stageFilter, reportMissing, q, years: yearArr ? [...yearArr].sort() : null, companyId: companyFilter ?? null }],
    queryFn: () =>
      salesApi.listSales({
        pageSize: FETCH_LIMIT,
        q: q || undefined,
        stage: reportMissing ? undefined : stageFilter,
        reportMissing: reportMissing || undefined,
        year: yearArr,
        companyId: companyFilter,
      }),
    placeholderData: keepPreviousData,
    // 사업자 전체 해제만 호출 차단 — 단계/연도 전체 해제여도 ❗이상 건 표시를 위해 조회.
    enabled: !allCompaniesOff,
  });

  const toggleStage = useCallback((s: SalesStatus) => {
    setActiveStages((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }, []);

  const toggleCompany = useCallback((c: CompanyId) => {
    setActiveCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  }, []);

  // 전체 해제 규칙 (2026-07-17 사용자 확정):
  //   · 단계/연도 전체 해제 → ❗이상 미완료 건만 표시. 이상 건은 원래 단계·연도 필터를
  //     무시하고 항상 표시되는 정책이라(백엔드 anomalyAlwaysVisible), 전체 해제여도 남는다.
  //   · 사업자 전체 해제 → 빈 목록 (사업자 필터는 이상 건에도 적용되는 기존 정책).
  // 「판보 미작성만」이 켜져 있으면 단계 토글은 안 쓰므로 단계 전체 해제로 ❗만 남기지 않는다
  const anomalyOnlyMode = !allCompaniesOff && ((allOff && !reportMissing) || selectedYears.size === 0);
  const fetchedRows = query.data?.data ?? [];
  const baseRows = allCompaniesOff
    ? []
    : anomalyOnlyMode
    ? fetchedRows.filter((r) => anomalyLabel(r) !== null)
    : fetchedRows;

  const toggleYear = useCallback((y: string) => {
    setSelectedYears((prev) => {
      const next = new Set(prev);
      if (next.has(y)) next.delete(y);
      else next.add(y);
      return next;
    });
  }, []);

  const yearOn = useCallback((y: string) => selectedYears.has(y), [selectedYears]);
  const allYearsOn =
    availableYears.length > 0 && selectedYears.size === availableYears.length;
  const allYearsOff = selectedYears.size === 0;

  // 정렬·필터는 보이는 컬럼 기준이지만 합계는 표시 여부와 무관 → baseRows 전체 컬럼 기준
  const visibleRows = useMemo(
    () => applyColState(baseRows, COLUMNS, colState),
    [baseRows, colState],
  );
  // 현재 필터링된 결과의 제안가 합계 — 헤더 위에 표시
  const proposalPriceSum = useMemo(
    () => visibleRows.reduce((sum, r) => sum + (r.proposalPrice || 0), 0),
    [visibleRows],
  );

  // 발행 합계 — invoiceDate NOT NULL 인 가시 행의 제안가 합 (실매출 보정용 참고치)
  const invoicedPriceSum = useMemo(
    () => visibleRows.reduce(
      (sum, r) => sum + (r.invoiceDate ? r.proposalPrice || 0 : 0),
      0,
    ),
    [visibleRows],
  );
  // 검색바 자동완성 후보 — 화면에 로드된 행에서 매출번호 / 엔드유저 / 거래처 / 프로젝트명 추출.
  const searchSuggestions = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const r of baseRows) {
      if (r.salesId) set.add(r.salesId);
      if (r.endUser) set.add(r.endUser);
      if (r.invoiceCompany) set.add(r.invoiceCompany);
      if (r.proposalCompany) set.add(r.proposalCompany);
      if (r.projectName) set.add(r.projectName);
    }
    return [...set];
  }, [baseRows]);

  // Row.memo 효과를 위해 onOpen 을 stable 하게 묶음 (부모의 setState 라 늘 같다).
  const handleOpen = useCallback((salesId: string) => {
    onOpen(salesId);
  }, [onOpen]);

  // 세금계산서 발행요청 — 선택 토글(Row.memo 안정성 위해 stable) + 선택 모드 종료.
  const toggleSelect = useCallback((salesId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(salesId)) next.delete(salesId);
      else next.add(salesId);
      return next;
    });
  }, []);
  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedIds(new Set());
  }, []);
  const allVisibleSelected =
    visibleRows.length > 0 && visibleRows.every((r) => selectedIds.has(r.salesId));
  // 선택된 건 중 이미 발행요청(🧾)을 보낸 건 수 — 철회 버튼 활성/라벨용.
  const selectedMarkedCount = baseRows.filter(
    (r) => selectedIds.has(r.salesId) && r.invoiceRequestAt,
  ).length;
  // 발행요청 철회 — 봇 메시지는 회수 불가 → 경영지원팀 방에 '철회 안내' 후속 전송 + 표식 해제.
  const handleWithdraw = useCallback(async () => {
    const markedIds = baseRows
      .filter((r) => selectedIds.has(r.salesId) && r.invoiceRequestAt)
      .map((r) => r.salesId);
    if (markedIds.length === 0) {
      window.alert('선택한 건 중 발행요청(🧾)을 보낸 건이 없습니다.');
      return;
    }
    if (
      !window.confirm(
        `선택한 ${markedIds.length}건의 세금계산서 발행요청을 철회합니다.\n` +
          '경영지원팀 방에 "철회 안내" 메시지가 전송되고, 🧾 표식이 해제됩니다.\n' +
          '(이미 보낸 원래 메시지는 메신저 특성상 자동 삭제되지 않습니다.)\n\n진행할까요?',
      )
    ) {
      return;
    }
    setWithdrawing(true);
    try {
      const res = await salesApi.withdrawTaxInvoiceRequest(markedIds);
      if (res.sent) {
        window.alert(`✅ 발행요청 철회 안내를 보냈습니다 (${res.count}건).`);
        exitSelectMode();
        qc.invalidateQueries({ queryKey: ['sales'] });
      } else {
        window.alert(`철회하지 못했습니다.\n\n${res.reason ?? '알 수 없는 사유'}`);
      }
    } catch (e) {
      window.alert(`철회 실패: ${(e as Error).message}`);
    } finally {
      setWithdrawing(false);
    }
  }, [baseRows, selectedIds, exitSelectMode, qc]);
  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allOn = visibleRows.length > 0 && visibleRows.every((r) => next.has(r.salesId));
      if (allOn) visibleRows.forEach((r) => next.delete(r.salesId));
      else visibleRows.forEach((r) => next.add(r.salesId));
      return next;
    });
  }, [visibleRows]);

  return (
    <div>
      {/* 페이지 헤더 */}
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-sm text-slate-500 mt-1 flex items-center gap-2">
            <span>
              {allCompaniesOff
                ? '총 0건'
                : anomalyOnlyMode
                ? `총 ${baseRows.length}건 · ❗항상 표시 건`
                : query.data
                ? `총 ${query.data.page.total}건` +
                  (visibleRows.length !== baseRows.length ? ` · 필터 후 ${visibleRows.length}건` : '')
                : '불러오는 중…'}
            </span>
            <VatNote />
          </div>
          <LastUpdateBadge className="mt-1" />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* 세금계산서 발행요청 — 선택 모드 토글 */}
          {canCreate && !selectMode && (
            <button
              type="button"
              onClick={() => setSelectMode(true)}
              title="발행할 영업건들을 골라 경영지원팀에 세금계산서 발행요청을 보냅니다"
              className="btn btn-success"
            >
              🧾 세금계산서 발행요청
            </button>
          )}
          {selectMode && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
              <span className="text-sm text-emerald-800 font-semibold whitespace-nowrap">
                {selectedIds.size}건 선택
              </span>
              <button
                type="button"
                onClick={() => setRequestOpen(true)}
                disabled={selectedIds.size === 0}
                className="btn btn-success-solid btn-sm"
              >
                다음 ▶
              </button>
              <button
                type="button"
                onClick={handleWithdraw}
                disabled={selectedMarkedCount === 0 || withdrawing}
                title="이미 보낸 발행요청을 철회합니다 (경영지원팀 방에 철회 안내 전송 + 🧾 해제)"
                className="btn btn-danger btn-sm"
              >
                {withdrawing ? '철회 중…' : `🗑 발행요청 철회${selectedMarkedCount ? ` (${selectedMarkedCount})` : ''}`}
              </button>
              <button
                type="button"
                onClick={exitSelectMode}
                className="btn btn-ghost btn-sm"
              >
                취소
              </button>
            </div>
          )}
          {query.data && (
            <div className="text-sm font-semibold text-slate-700">
              제안가 합계 {isViewer ? '***' : `₩${fmtKrw(proposalPriceSum)}`}
              <span className="text-slate-400 font-normal"> · </span>
              발행 합계 <span className="text-blue-700">{isViewer ? '***' : `₩${fmtKrw(invoicedPriceSum)}`}</span>
            </div>
          )}
          <button
            type="button"
            onClick={async () => {
              setExcelDownloading(true);
              try {
                // 운영 서식 (영업진행 데이터.xlsx) 템플릿 기반 — 백엔드에서 채워 다운로드.
                // 필터·정렬은 visibleRows 그대로 반영, 컬럼 토글은 무시 (템플릿 컬럼 그대로).
                await salesApi.downloadSalesExcel(visibleRows);
              } catch (e) {
                window.alert(`엑셀 다운로드 실패: ${(e as Error).message}`);
              } finally {
                setExcelDownloading(false);
              }
            }}
            disabled={!canCreate || excelDownloading || visibleRows.length === 0}
            title={
              canCreate
                ? '운영 영업진행 서식으로 엑셀 다운로드 (현재 필터·정렬된 행)'
                : 'sales 또는 admin 권한이 필요합니다'
            }
            className={`btn ${!canCreate
                ? 'border-slate-200 text-slate-300 cursor-not-allowed'
                : excelDownloading ? 'btn-secondary' : 'btn-secondary'}`}
          >
            {excelDownloading ? '⏳ 다운로드 중…' : '📥 엑셀'}
          </button>
          {canNtsCheck && (
            <button
              type="button"
              onClick={() => setNtsCheckOpen(true)}
              title="홈택스 매출전자세금계산서 목록(.xls)과 포털 발행내역을 대조합니다 — 확인만, 수정·생성 없음"
              className="btn btn-outline"
            >
              🔍 국세청 발행 대조
            </button>
          )}
          <button
            type="button"
            onClick={() => setCreating(true)}
            disabled={!canCreate}
            title={canCreate ? '신규 영업건 등록' : 'sales 또는 admin 권한이 필요합니다'}
            className="btn btn-primary"
          >
            + 영업건 등록
          </button>
        </div>
      </div>

      {/* 검색 + 진행단계 탭 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-4 mb-4">
        <SearchBar
          value={q}
          onCommit={setQ}
          placeholder="🔍 매출번호 / 엔드유저 / 거래처 / 프로젝트명"
          className="mb-3"
          suggestions={searchSuggestions}
        />

        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`flex gap-1 flex-wrap ${reportMissing ? 'opacity-40' : ''}`}>
              {ALL_STAGES.map((s) => {
                const on = activeStages.has(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleStage(s)}
                    disabled={reportMissing}
                    aria-pressed={on}
                    title={reportMissing ? '「📄 판보 미작성만」이 켜져 있어 진행단계는 「발행」으로 정해져 있어요 — 끄면 다시 고를 수 있어요' : on ? '클릭하여 숨기기' : '클릭하여 표시'}
                    className={`px-3 py-1 rounded text-sm transition ${
                      on
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-400 line-through hover:bg-slate-200'
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            {/* 진행단계 빠른 토글 — 판보 바로 옆 */}
            <div className="flex items-center gap-1">
              {!allOn && (
                <button
                  type="button"
                  onClick={() => setActiveStages(new Set(ALL_STAGES))}
                  className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                >
                  전체 선택
                </button>
              )}
              {!allOff && (
                <button
                  type="button"
                  onClick={() => setActiveStages(new Set())}
                  className="text-xs text-slate-500 hover:underline whitespace-nowrap"
                >
                  전체 해제
                </button>
              )}
            </div>
            {/* 📄 판보 미작성만 (v2.305.0 · 개발요청 #20) — 발행 중 판보(자체·묶음·과거 엑셀)가 없는 건만 */}
            <button
              type="button"
              onClick={() => setReportMissing((v) => !v)}
              aria-pressed={reportMissing}
              title={reportMissing
                ? '켜짐 — 계산서 발행 후 판매보고서가 아직 없는 건만 보여요(묶음 판보·과거 엑셀 판보가 있는 건은 빠짐). 다시 누르면 꺼져요'
                : '계산서 발행 후 판매보고서가 아직 없는 건만 보기 — 유보 사유는 행의 💬 에 마우스를 올리면 보여요'}
              className={`px-2.5 py-1 rounded text-sm whitespace-nowrap transition border ${
                reportMissing
                  ? 'bg-amber-500 text-white border-amber-500 shadow-sm'
                  : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
              }`}
            >
              📄 판보 미작성만
            </button>
            {/* 사업자 다중 토글 — 진행단계와 시각적 간격 띄움 (ml-6) */}
            <div className="flex items-center gap-1.5 pl-3 ml-6 border-l border-slate-200">
              <span className="text-xs text-slate-500">사업자</span>
              <div className="flex gap-1">
                {ALL_COMPANIES.map((c) => {
                  const on = activeCompanies.has(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCompany(c)}
                      aria-pressed={on}
                      title={on ? '클릭하여 숨기기' : '클릭하여 표시'}
                      className={`px-2.5 py-1 rounded text-sm transition ${
                        on
                          ? 'bg-slate-700 text-white shadow-sm'
                          : 'bg-slate-100 text-slate-400 line-through hover:bg-slate-200'
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          {/* 연도 필터 — 드롭다운 (데이터 로드 후에만 표시) */}
          {availableYears.length > 1 && (
            <YearDropdown
              availableYears={availableYears}
              selectedYears={selectedYears}
              onToggle={toggleYear}
              onSelectAll={() => setSelectedYears(new Set(availableYears))}
              onClearAll={() => setSelectedYears(new Set())}
              yearOn={yearOn}
              allYearsOn={allYearsOn}
              allYearsOff={allYearsOff}
            />
          )}
          {/* 우측 묶음 — [필터·정렬 초기화 ▼] [📐 내 표 설정 | ↺ 초기화] */}
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {/* v2.203.0: 「5단계」는 틀린 글자였다 — 기본으로 켜지는 단계는 일반·유력·발주·납품 4개다(DEFAULT_ACTIVE_STAGES) */}
            <ResetMenu
              defaultLabel={`진행중 ${DEFAULT_ACTIVE_STAGES.length}단계 (발행·판보 제외), 최근 ${DEFAULT_YEAR_COUNT}개년`}
              onResetDefault={() => {
                colState.resetAll();
                setQ('');
                setActiveStages(new Set(DEFAULT_ACTIVE_STAGES));
                setActiveCompanies(new Set(ALL_COMPANIES));
                setSelectedYears(defaultYears(availableYears));
              }}
              onResetAll={() => {
                colState.resetAll();
                setQ('');
                setActiveStages(new Set(ALL_STAGES));
                setActiveCompanies(new Set(ALL_COMPANIES));
                if (availableYears.length > 0) {
                  setSelectedYears(new Set(availableYears));
                }
              }}
            />
            <ExcelModeGroup
              enabled={excelMode}
              hasPrefs={hasPrefs}
              userName={user?.name ?? '사용자'}
              onChange={(on) => patchPrefs.mutate({ salesTabExcelMode: on })}
              onReset={handleExcelModeReset}
            />
          </div>
        </div>

        {/* 컬럼 표시 토글 — 📐 내 표 설정 ON 일 때만 노출. 모바일 hidden. */}
        {excelMode && (
          <div className="hidden md:block mt-3 border-t border-slate-100 pt-2 text-xs">
            <div className="flex items-center justify-between mb-1">
              <button
                type="button"
                onClick={() => setColMenuOpen(v => !v)}
                className="text-blue-600 hover:underline"
              >
                ⚙ 표시 컬럼 ({visibleColumns.length}/{fullAllKeys.length}{patchPrefs.isPending ? ' · 저장 중...' : ''})
                {colMenuOpen ? ' ▲' : ' ▼'}
              </button>
              <span className="text-xs text-slate-500">
                💡 컬럼 헤더를 끌면 순서 변경 · 우측 끝을 끌면 너비 조정 (자동 저장)
              </span>
            </div>
            {colMenuOpen && (
              <div className="mt-2 grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1">
                {fullColumns.map((d) => {
                  const on = activeColKeys.has(d.key);
                  return (
                    <label
                      key={d.key}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-xs ${
                        on ? 'bg-blue-50 text-blue-800' : 'text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggleColumn(d.key)}
                        disabled={patchPrefs.isPending}
                      />
                      <span>{d.toggleLabel ?? d.label}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 표 — 페이지 구분 없이 전체. 컬럼 헤더 + 합계 행 sticky (wrapper 가 scroll container)
          높이: 창 아래 끝까지(v2.249.0 · 표 위치를 재서) — 폰은 옛 고정 계산 그대로 */}
      <div
        ref={tableBoxRef}
        className="bg-white rounded shadow-sm border border-slate-200 overflow-auto"
        style={{ maxHeight: fillHeight !== null ? `${fillHeight}px` : 'calc(100vh - 320px)' }}
      >
        <table className="w-full text-[13px] border-collapse min-[1920px]:whitespace-nowrap">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-20">
            <tr className="text-slate-700">
              {selectMode && (
                <th className="px-2 w-10 text-center align-middle">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    title="보이는 전체 선택/해제"
                  />
                </th>
              )}
              {visibleColumns.map((col, idx) => (
                <ColumnHeader
                  key={col.key}
                  column={col}
                  baseRows={baseRows}
                  // 연쇄 필터 — applyColState 에 넘기는 것과 **같은 컬럼 집합**이어야 한다
                  // (숨긴 컬럼의 필터도 표에는 걸려 있으므로 목록에도 반영돼야 함)
                  columns={COLUMNS}
                  colState={colState}
                  excelMode={excelMode}
                  onReorder={excelMode ? handleReorder : undefined}
                  onMoveLeft={excelMode && idx > 0 ? () => handleMoveLeft(col.key) : undefined}
                  onMoveRight={
                    excelMode && idx < visibleColumns.length - 1
                      ? () => handleMoveRight(col.key)
                      : undefined
                  }
                  onResize={excelMode ? handleResize : undefined}
                  widthPx={columnWidths?.[col.key]}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {query.isLoading && !query.data && (
              <tr>
                <td colSpan={visibleColumns.length + (selectMode ? 1 : 0)} className="text-center py-8 text-slate-400"><LoadingLines /></td>
              </tr>
            )}
            {query.isError && (
              <tr>
                <td colSpan={visibleColumns.length + (selectMode ? 1 : 0)} className="text-center py-8 text-red-500">
                  오류: {(query.error as Error).message}
                </td>
              </tr>
            )}
            {/* v2.317.0 · 빈 상태 한 벌 — 무엇이 없는지 + 다음에 할 일(단추 2개 이하) */}
            {!query.isLoading && visibleRows.length === 0 && (
              <EmptyRow
                colSpan={visibleColumns.length + (selectMode ? 1 : 0)}
                title="이 조건에 맞는 영업건이 없어요"
                hint="거른 단계·사업자·연도·열 필터를 풀거나 검색어를 지워 보세요"
                actions={(
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        colState.resetAll();
                        setQ('');
                        setActiveStages(new Set(DEFAULT_ACTIVE_STAGES));
                        setActiveCompanies(new Set(ALL_COMPANIES));
                        setSelectedYears(defaultYears(availableYears));
                      }}
                    >
                      ↺ 필터·정렬 초기화
                    </button>
                    {canCreate && (
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
                        + 영업건 등록
                      </button>
                    )}
                  </>
                )}
              />
            )}
            {visibleRows.map((row) => (
              <Row
                key={row.salesId}
                row={row}
                columns={visibleColumns}
                onOpen={handleOpen}
                selectMode={selectMode}
                selected={selectedIds.has(row.salesId)}
                onToggleSelect={toggleSelect}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* 표 하단 행 수 */}
      {!allCompaniesOff && query.data && (anomalyOnlyMode ? visibleRows.length > 0 : query.data.page.total > 0) && (
        <div className="mt-3 text-xs text-slate-500 text-right">
          {visibleRows.length}건 표시
          {!anomalyOnlyMode &&
            visibleRows.length !== query.data.page.total &&
            ` (전체 ${query.data.page.total}건 중)`}
        </div>
      )}

      {/* 모달은 lazy — 처음 열 때 동적 로드. 초기 번들에서 분리.
          상세 모달은 SalesListPage 에 있다(v2.249.0 — 판매보고서 탭과 공용). */}
      <Suspense fallback={null}>
        {/* 등록 모달 (Phase 6-3) — 저장하면 그 건의 상세를 연다 */}
        {creating && (
          <SalesFormModal
            mode="create"
            onClose={() => setCreating(false)}
            onSaved={(newId) => {
              setCreating(false);
              onOpen(newId);
            }}
          />
        )}

        {/* 국세청 매출계산서 발행내역 대조 — 확인 전용 (쓰기 없음) */}
        {ntsCheckOpen && (
          <NtsSalesCheckModal onClose={() => setNtsCheckOpen(false)} />
        )}

        {/* 세금계산서 발행요청 — 선택한 영업건 자동완성 → 네이버웍스 전송 */}
        {requestOpen && (
          <TaxInvoiceRequestModal
            salesIds={[...selectedIds]}
            onClose={() => setRequestOpen(false)}
            onSent={() => {
              setRequestOpen(false);
              exitSelectMode();
              qc.invalidateQueries({ queryKey: ['sales'] });
            }}
          />
        )}
      </Suspense>
    </div>
  );
}

/**
 * 줄 바탕 = 진행단계별 색 (v2.319.1 · 사용자 「상태에 따른 배경색 표시를 기존처럼」 — v2.317.2 가 흰 바탕으로 바꾼 것을 되돌림).
 *   - 유력: 초록계열 · 발주: 노란계열 · 납품: 푸른계열 — 마우스 올리면 같은 계열 더 진하게(v2.317.1 까지의 값 그대로)
 *   - 나머지(일반·발행·판보): Salesforce 식 흰 바탕 + 회색 호버(v2.317.2 · 표 B) — 전에도 이 셋은 색이 없었다.
 *   단계 꼬리표(「진행」 칸)는 그대로 — 바탕색과 꼬리표가 같은 단계를 두 번 말한다.
 */
const ROW_BG_BY_STATUS: Partial<Record<SalesStatus, string>> = {
  유력: 'bg-emerald-50 even:bg-emerald-100/40 hover:bg-emerald-200',
  발주: 'bg-amber-50 even:bg-amber-100/40 hover:bg-amber-200',
  납품: 'bg-blue-50 even:bg-blue-100/40 hover:bg-blue-200',
};
const ROW_BG_DEFAULT = 'hover:bg-slate-50';

// React.memo — 부모 렌더 시 다른 행 재렌더 skip. onOpen 은 useCallback 으로 stable.
// 매입 PurchaseList 와 동일 패턴. 1000+ 행 hover/scroll 부드러움 개선.
// columns 는 사용자별 prefs 로 가변. activeColKeys 가 같으면 reference 동일 → 재렌더 skip.
/**
 * ❗ 이상 미완료 판정 — 백엔드 list.ts 의 anomalyAlwaysVisible 과 동일 조건.
 * 해당 건은 연도·단계 필터와 무관하게 항상 응답에 포함되고, 행 앞에 ❗가 붙는다.
 * 발주·납품 상태로 정상 진행 중(1년 미만)인 건은 null (느낌표 없음 · 일반 필터 정책).
 * @returns 툴팁 문구 (정상이면 null)
 */
function anomalyLabel(row: SalesListRow): string | null {
  if (row.status !== '발주' && row.status !== '납품' && row.status !== '발행') return null;
  // A. 발행됐는데 납품이 안 됨 (순서 역전)
  if (row.status === '발행' && !row.deliveryDate) return '발행됐는데 납품이 안 된 건';
  const yearAgo = new Date();
  yearAgo.setFullYear(yearAgo.getFullYear() - 1);
  // B. 발주(계약일) 후 1년 경과 — 납품 미완료 (계약일 없으면 매출번호 연도로 2년 이상만)
  if (!row.deliveryDate) {
    if (row.contractDate && new Date(row.contractDate) <= yearAgo) {
      return '발주(계약) 후 1년 경과 — 납품 미완료';
    }
    const idYear = Number(row.salesId.slice(0, 4));
    if (!row.contractDate && Number.isFinite(idYear) && idYear <= new Date().getFullYear() - 2) {
      return '발주 후 1년 이상 경과 — 납품 미완료';
    }
  }
  // C. 납품 후 1년 경과 — 계산서 미발행
  if (!row.invoiceDate && row.deliveryDate && new Date(row.deliveryDate) <= yearAgo) {
    return '납품 후 1년 경과 — 계산서 미발행';
  }
  return null;
}

const Row = memo(function Row({
  row,
  columns,
  onOpen,
  selectMode,
  selected,
  onToggleSelect,
}: {
  row: SalesListRow;
  columns: SalesColumn[];
  onOpen: (salesId: string) => void;
  selectMode: boolean;
  selected: boolean;
  onToggleSelect: (salesId: string) => void;
}) {
  const bgCls = ROW_BG_BY_STATUS[row.status] ?? ROW_BG_DEFAULT;
  const anomaly = anomalyLabel(row);
  const selectedCls = selectMode && selected ? '!bg-emerald-200' : '';
  return (
    <tr
      className={`border-b border-slate-100 cursor-pointer transition-colors ${bgCls} ${selectedCls}`}
      onClick={() => (selectMode ? onToggleSelect(row.salesId) : onOpen(row.salesId))}
    >
      {selectMode && (
        <td
          className="px-2 text-center border-r border-slate-100"
          onClick={(e) => e.stopPropagation()}
        >
          <input type="checkbox" checked={selected} onChange={() => onToggleSelect(row.salesId)} />
        </td>
      )}
      {columns.map((col, i) => {
        const alignCls =
          col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';
        const hiddenCls = col.mobileHidden ? 'hidden md:table-cell' : '';
        return (
          <td
            key={col.key}
            className={`px-2 py-1 border-b border-slate-200 ${alignCls} ${hiddenCls}`}
          >
            {i === 0 && anomaly && (
              <span
                className="text-red-600 font-bold mr-0.5"
                title={`❗ ${anomaly} (연도·단계 필터와 무관하게 항상 표시)`}
              >❗</span>
            )}
            {col.render ? col.render(row) : String(col.accessor(row) ?? '')}
          </td>
        );
      })}
    </tr>
  );
});
